import { useMemo } from "react";
import Chart from "react-apexcharts";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Divider,
} from "@mui/material";
import { useWeekly } from "@/services/useDashboard";
import { ApexOptions } from "apexcharts";

interface Props {
  startDate?: Date | null;
  endDate?: Date | null;
  userId?: string;
  userSelected?: string;
  projectSelected?: string;
  type?: string;
  region?: string;
  cordination?: string;
  principal?: string;
  master?: string;
}

const WeeklyReportDashboard: React.FC<Props> = ({
  startDate,
  endDate,
  userId,
  userSelected,
  projectSelected,
  type,
  region,
  cordination,
  principal,
}) => {
  const filters = useMemo(
    () => ({
      startDate,
      endDate,
      userId,
      userSelected,
      projectSelected,
      type,
      region,
      cordination,
      principal,
    }),
    [
      startDate,
      endDate,
      userId,
      userSelected,
      projectSelected,
      type,
      region,
      cordination,
      principal,
    ]
  );

  const { data: weeklyReports = [] } = useWeekly(filters);
  const categories = weeklyReports.map((r) => r.label);

  const valueSeries = [
    {
      name: "Leads",
      data: weeklyReports.map((r) => r.leadsCount),
    },
    {
      name: "Agendadas",
      data: weeklyReports.map((r) => r.dealsWithAppointment),
    },
    {
      name: "Confirmadas",
      data: weeklyReports.map((r) => r.dealsWithConfirmedAppointment),
    },
    {
      name: "Asistencias",
      data: weeklyReports.map((r) => r.dealsArrived),
    },
    {
      name: "Cierres",
      data: weeklyReports.map((r) => r.dealsWon),
    },
  ];

  const percentageSeries = [
    {
      name: "Leads",
      data: weeklyReports.map((r) =>
        parseFloat(r.leadsEfectivity.replace("%", ""))
      ),
    },
    {
      name: "Agendas",
      data: weeklyReports.map((r) =>
        parseFloat(r.dealsAppointmentEfectivity.replace("%", ""))
      ),
    },
    {
      name: "Asistencias",
      data: weeklyReports.map((r) =>
        parseFloat(r.dealsArrivedEfectivity.replace("%", ""))
      ),
    },
    {
      name: "Cierres",
      data: weeklyReports.map((r) =>
        parseFloat(r.dealsWonEfectivity.replace("%", ""))
      ),
    },
  ];

  const barOptions: ApexOptions = {
    chart: {
      type: "bar",
      stacked: false,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "60%",
      },
    },
    xaxis: {
      categories,
    },
    yaxis: {
      title: { text: "Cantidad" },
    },
    legend: {
      position: "top",
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
  };

  const lineOptions: ApexOptions = {
    chart: {
      type: "line",
    },
    xaxis: {
      categories,
    },
    yaxis: {
      title: {
        text: "Efectividad (%)",
      },
      max: 100,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val.toFixed(2)}%`,
      },
    },
    stroke: {
      curve: "smooth",
    },
    markers: {
      size: 4,
    },
  };

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Reporte Semanal - Totales
      </Typography>
      <Chart
        options={barOptions}
        series={valueSeries}
        type="bar"
        height={400}
      />

      <Divider sx={{ my: 4 }} />

      <Typography variant="h5" gutterBottom>
        Reporte Semanal - Efectividad (%)
      </Typography>
      <Chart
        options={lineOptions}
        series={percentageSeries}
        type="line"
        height={400}
      />

      <Divider sx={{ my: 4 }} />

      <Typography variant="h5" gutterBottom>
        Resumen Semanal (%)
      </Typography>
      <Grid container spacing={2}>
        {weeklyReports.map((week, index) => (
          <Grid sx={{ xs: 12, md: 6, lg: 4 }} key={index}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {week.label}
                </Typography>
                <Typography variant="body2">
                  Efectividad de Leads: <strong>{week.leadsEfectivity}</strong>
                </Typography>
                <Typography variant="body2">
                  Efectividad de Agendas:{" "}
                  <strong>{week.dealsAppointmentEfectivity}</strong>
                </Typography>
                <Typography variant="body2">
                  Efectividad de Asistencias:{" "}
                  <strong>{week.dealsArrivedEfectivity}</strong>
                </Typography>
                <Typography variant="body2">
                  Efectividad de Cierres:{" "}
                  <strong>{week.dealsWonEfectivity}</strong>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default WeeklyReportDashboard;
